import {
  ArrowLeft,
  Ban,
  FilePlus2,
  HandCoins,
  Info,
  LoaderCircle,
  ShoppingBasket,
  TriangleAlert,
} from 'lucide-react'
import {
  useCallback,
  useEffect,
  useState,
} from 'react'
import {
  useNavigate,
  useParams,
} from 'react-router-dom'

import AnularPagoModal from '../../components/cuentasPorPagar/AnularPagoModal'
import AsociarDocumentoModal from '../../components/cuentasPorPagar/AsociarDocumentoModal'
import EstadoFinancieroBadge from '../../components/cuentasPorPagar/EstadoFinancieroBadge'
import RegistrarPagoModal from '../../components/cuentasPorPagar/RegistrarPagoModal'
import { useEmpresa } from '../../context/EmpresaContext'
import { cargarDetalleCuentaPorPagar } from '../../services/cuentasPorPagar'
import {
  obtenerEstadoRecepcion,
  obtenerModalidad,
} from '../../utils/abastecimiento'
import {
  formatearKilosCompra,
  nombreProveedorCompra,
  obtenerEstadoCompra,
} from '../../utils/compras'
import {
  ESTADOS_PAGO,
  etiquetaMedioPago,
  etiquetaRespaldo,
  filaAdmitePago,
  puedeAnularPagoCompra,
  puedeAsociarDocumentoPago,
  puedeRegistrarPagoCompra,
} from '../../utils/cuentasPorPagar'
import {
  formatearFechaPos,
  formatearMontoPos,
} from '../../utils/pos'

function Dato({ etiqueta, children }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">
        {etiqueta}
      </dt>

      <dd className="mt-0.5 break-words font-semibold text-slate-900">
        {children}
      </dd>
    </div>
  )
}

function Seccion({ titulo, children }) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
      <h3 className="mb-4 text-sm font-semibold uppercase tracking-wide text-slate-500">
        {titulo}
      </h3>

      {children}
    </section>
  )
}

function textoCondicionPago(compra) {
  if (compra.condicion_pago === 'CRÉDITO') {
    return `Crédito a ${compra.plazo_dias} días`
  }

  if (compra.condicion_pago === 'CONTADO') return 'Contado'

  return 'No informada'
}

function formatearFechaHora(valor) {
  if (!valor) return '—'

  return new Date(valor).toLocaleString('es-CL', {
    dateStyle: 'short',
    timeStyle: 'short',
  })
}

function textoDocumento(pago) {
  const tipo = etiquetaRespaldo(pago.tipo_respaldo)

  return pago.numero_documento ? `${tipo} N° ${pago.numero_documento}` : tipo
}

function DetalleCuentaPorPagar() {
  const { compraId } = useParams()
  const navigate = useNavigate()
  const { empresaActual } = useEmpresa()

  const empresaId = empresaActual?.id
  const rol = empresaActual?.rol
  const puedePagar = puedeRegistrarPagoCompra(rol)
  const puedeDocumento = puedeAsociarDocumentoPago(rol)
  const puedeAnular = puedeAnularPagoCompra(rol)

  const [detalle, setDetalle] = useState(null)
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState('')
  const [pagoAbierto, setPagoAbierto] = useState(false)
  const [pagoDocumento, setPagoDocumento] = useState(null)
  const [pagoAnular, setPagoAnular] = useState(null)

  const cargar = useCallback(async () => {
    if (!empresaId || !compraId) return

    setCargando(true)
    setError('')

    try {
      const resultado = await cargarDetalleCuentaPorPagar(
        empresaId,
        compraId
      )

      if (!resultado) {
        setDetalle(null)
        setError('La compra no existe o pertenece a otra empresa.')
        return
      }

      setDetalle(resultado)
    } catch (errorCarga) {
      console.error('Error al cargar cuenta por pagar:', errorCarga)
      setError(
        errorCarga.message || 'No fue posible cargar la cuenta por pagar.'
      )
    } finally {
      setCargando(false)
    }
  }, [empresaId, compraId])

  useEffect(() => {
    cargar()
  }, [cargar])

  const volver = () => navigate('/contabilidad/cuentas-por-pagar')

  // Tras registrar, anular o asociar: se recarga saldo e historial.
  const alCambiar = () => {
    setPagoAbierto(false)
    setPagoDocumento(null)
    setPagoAnular(null)
    cargar()
  }

  if (cargando && !detalle) {
    return (
      <div className="flex min-h-64 items-center justify-center">
        <LoaderCircle
          size={32}
          className="animate-spin text-emerald-600"
        />
      </div>
    )
  }

  if (error || !detalle) {
    return (
      <div className="rounded-2xl border border-red-200 bg-white p-8 text-center shadow-sm">
        <h3 className="text-lg font-bold text-red-600">
          No fue posible cargar la cuenta por pagar
        </h3>

        <p className="mt-2 text-slate-600">{error}</p>

        <button
          type="button"
          onClick={volver}
          className="mt-4 min-h-11 text-sm font-semibold text-emerald-700"
        >
          Volver a Cuentas por Pagar
        </button>
      </div>
    )
  }

  const { compra, recepcion, fila, pagos } = detalle
  const modalidad = obtenerModalidad(compra.modalidad)
  const estadoCompra = obtenerEstadoCompra(compra.estado)
  const estadoRecepcion = obtenerEstadoRecepcion(recepcion)
  const puedeRegistrar = puedePagar && filaAdmitePago(fila)
  const tipo = fila?.clasificacion.tipo

  const etiquetaTotal =
    tipo === 'COMPROMISO'
      ? 'Compromiso estimado'
      : tipo === 'PROVISIONAL'
        ? 'Total provisional'
        : 'Total de la obligación'

  return (
    <div className="w-full space-y-6">
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-start">
        <div>
          <button
            type="button"
            onClick={volver}
            className="mb-2 inline-flex min-h-11 items-center gap-2 text-sm font-semibold text-slate-500 hover:text-slate-700"
          >
            <ArrowLeft size={16} />
            Volver a Cuentas por Pagar
          </button>

          <p className="text-sm font-semibold uppercase tracking-wide text-emerald-600">
            Cuenta por pagar
          </p>

          <h2 className="mt-1 text-2xl font-bold text-slate-900 sm:text-3xl">
            {compra.codigo_compra}
          </h2>

          <p className="mt-1 text-slate-500">
            {nombreProveedorCompra(compra)}
          </p>
        </div>

        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={() => navigate(`/compras/${compra.id}`)}
            className="flex min-h-12 flex-1 items-center justify-center gap-2 rounded-lg border border-slate-300 px-5 py-3 font-semibold text-slate-700 transition hover:bg-slate-100 sm:flex-none"
          >
            <ShoppingBasket size={18} />
            Ver compra
          </button>

          {puedeRegistrar && (
            <button
              type="button"
              onClick={() => setPagoAbierto(true)}
              className="flex min-h-12 flex-1 items-center justify-center gap-2 rounded-lg bg-emerald-600 px-5 py-3 font-semibold text-white transition hover:bg-emerald-700 sm:flex-none"
            >
              <HandCoins size={18} />
              Registrar pago
            </button>
          )}
        </div>
      </div>

      {!fila && (
        <p className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
          Esta compra no es una deuda activa
          {compra.estado === 'anulada'
            ? ' porque está anulada'
            : ' porque la recepción fue rechazada totalmente'}
          .
        </p>
      )}

      {fila?.requiereConciliacionFactura && (
        <div className="flex gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <TriangleAlert
            size={20}
            className="mt-0.5 shrink-0"
          />

          <p>
            La factura {fila.factura.numero} (
            {formatearMontoPos(fila.factura.montoCentavos / 100)}) difiere
            del total de las compras asociadas en{' '}
            {formatearMontoPos(
              Math.abs(fila.factura.diferenciaCentavos || 0) / 100
            )}
            . Revisa la conciliación entre factura y compra. La deuda se
            controla por la compra, no por la factura.
          </p>
        </div>
      )}

      <Seccion titulo="Información de compra">
        <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Dato etiqueta="Compra">
            {compra.codigo_compra} · {formatearFechaPos(compra.fecha_compra)}
          </Dato>
          <Dato etiqueta="Proveedor">{nombreProveedorCompra(compra)}</Dato>
          <Dato etiqueta="Producto">{compra.producto_nombre || '—'}</Dato>
          <Dato etiqueta="Modalidad">{modalidad.etiqueta}</Dato>
          <Dato etiqueta="Estado de la compra">{estadoCompra.etiqueta}</Dato>
          <Dato etiqueta="Estado de recepción">
            {compra.recepcion_automatica_habilitada === false
              ? 'Compra histórica'
              : estadoRecepcion.etiqueta}
          </Dato>
          <Dato etiqueta="Monto de compra">
            {formatearMontoPos(
              compra.modalidad === 'PREVIA' && !recepcion
                ? compra.monto_estimado
                : compra.monto_logistico_registrado
            )}
          </Dato>
          <Dato etiqueta="Condición de pago">
            {textoCondicionPago(compra)}
          </Dato>
          {compra.modalidad === 'PREVIA' && (
            <Dato etiqueta="Kilos acordados">
              {formatearKilosCompra(compra.cantidad_acordada_kg)}
            </Dato>
          )}
          {fila?.factura && (
            <Dato etiqueta="Factura asociada">{fila.factura.numero}</Dato>
          )}
        </dl>
      </Seccion>

      {fila && (
        <Seccion titulo="Información financiera">
          {tipo === 'COMPROMISO' && (
            <p className="mb-4 rounded-lg border border-violet-200 bg-violet-50 px-3 py-2 text-sm text-violet-800">
              Compromiso estimado (kilos acordados × precio por kilo). Se
              convierte en deuda cuando la recepción se valida. No admite
              pagos ni anticipos.
            </p>
          )}

          {tipo === 'PROVISIONAL' && (
            <p className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              Monto provisional: la compra no tiene recepción validada o es
              histórica. No admite pagos hasta tener una obligación
              definitiva.
            </p>
          )}

          {tipo === 'OBLIGACION' && (
            <p className="mb-4 text-xs text-slate-500">
              El abonado y el saldo corresponden solo a pagos registrados en
              Franco Frutt; pagos hechos fuera del sistema requieren
              conciliación.
            </p>
          )}

          <dl className="grid grid-cols-2 gap-4 lg:grid-cols-5">
            <Dato etiqueta={etiquetaTotal}>
              {formatearMontoPos(fila.totalCentavos / 100)}
            </Dato>
            <Dato etiqueta="Total abonado">
              {fila.abonadoCentavos === null
                ? '—'
                : formatearMontoPos(fila.abonadoCentavos / 100)}
            </Dato>
            <Dato etiqueta="Saldo pendiente">
              {fila.saldoCentavos === null
                ? '—'
                : formatearMontoPos(fila.saldoCentavos / 100)}
            </Dato>
            <Dato etiqueta="Vencimiento">
              {formatearFechaPos(fila.vencimiento)}
            </Dato>
            <Dato etiqueta="Estado financiero">
              <EstadoFinancieroBadge fila={fila} />
            </Dato>
          </dl>
        </Seccion>
      )}

      <Seccion titulo={`Historial de pagos (${pagos.length})`}>
        {pagos.length === 0 ? (
          <div className="flex gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
            <Info
              size={20}
              className="mt-0.5 shrink-0"
            />

            <p>
              Esta compra no tiene pagos registrados en Franco Frutt. Eso no
              significa que esté impaga: pudo pagarse fuera del sistema.
            </p>
          </div>
        ) : (
          <div className="w-full overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-slate-200 text-xs font-semibold uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="py-2 pr-3">Fecha</th>
                  <th className="py-2 pr-3 text-right">Monto</th>
                  <th className="py-2 pr-3">Medio</th>
                  <th className="py-2 pr-3">Referencia</th>
                  <th className="py-2 pr-3">Documento</th>
                  <th className="py-2 pr-3">Observaciones</th>
                  <th className="py-2 pr-3">Registrado por</th>
                  <th className="py-2 pr-3">Estado</th>
                  <th className="py-2 text-right">Acciones</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-100">
                {pagos.map((pago) => {
                  const anulado = pago.estado === 'ANULADO'
                  const estado = ESTADOS_PAGO[pago.estado]

                  return (
                    <tr key={pago.id}>
                      <td
                        className={`whitespace-nowrap py-2 pr-3 ${
                          anulado ? 'text-slate-400' : ''
                        }`}
                      >
                        {formatearFechaPos(pago.fecha_pago)}
                      </td>
                      <td
                        className={`whitespace-nowrap py-2 pr-3 text-right tabular-nums ${
                          anulado ? 'text-slate-400 line-through' : ''
                        }`}
                      >
                        {formatearMontoPos(pago.monto)}
                      </td>
                      <td className="py-2 pr-3">
                        {etiquetaMedioPago(pago.medio_pago)}
                      </td>
                      <td className="py-2 pr-3">{pago.referencia || '—'}</td>
                      <td className="py-2 pr-3">{textoDocumento(pago)}</td>
                      <td className="max-w-48 break-words py-2 pr-3">
                        {pago.observaciones || '—'}
                      </td>
                      <td className="py-2 pr-3">
                        {pago.creado_por_nombre}
                        <span className="block text-xs text-slate-400">
                          {formatearFechaHora(pago.creado_en)}
                        </span>
                      </td>
                      <td className="py-2 pr-3">
                        <span
                          className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${estado?.clase}`}
                        >
                          {estado?.etiqueta || pago.estado}
                        </span>

                        {anulado && (
                          <span className="mt-1 block max-w-48 break-words text-xs text-slate-500">
                            {pago.motivo_anulacion}
                            {pago.anulado_por_nombre &&
                              ` — ${pago.anulado_por_nombre}`}
                          </span>
                        )}
                      </td>
                      <td className="py-2">
                        {!anulado && (
                          <div className="flex justify-end gap-2">
                            {puedeDocumento && (
                              <button
                                type="button"
                                onClick={() => setPagoDocumento(pago)}
                                className="inline-flex min-h-9 items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-100"
                              >
                                <FilePlus2 size={14} />
                                Documento
                              </button>
                            )}

                            {puedeAnular && (
                              <button
                                type="button"
                                onClick={() => setPagoAnular(pago)}
                                className="inline-flex min-h-9 items-center gap-1.5 rounded-lg border border-red-300 px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-50"
                              >
                                <Ban size={14} />
                                Anular
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </Seccion>

      <RegistrarPagoModal
        abierto={pagoAbierto}
        fila={fila}
        empresaId={empresaId}
        onCerrar={() => setPagoAbierto(false)}
        onRegistrado={alCambiar}
      />

      <AsociarDocumentoModal
        pago={pagoDocumento}
        empresaId={empresaId}
        onCerrar={() => setPagoDocumento(null)}
        onActualizado={alCambiar}
      />

      <AnularPagoModal
        pago={pagoAnular}
        empresaId={empresaId}
        onCerrar={() => setPagoAnular(null)}
        onAnulado={alCambiar}
      />
    </div>
  )
}

export default DetalleCuentaPorPagar
